import { AUTH_ISSUER, BACKEND_URL, AE_BACKEND_URL, CDN_HOSTNAME } from "@/config";

export const getAuthIssuer = () =>
  AUTH_ISSUER || (process.env.REACT_APP_AUTH_ISSUER || "/auth").replace(/\/+$/, "");

export const isCdnUrl = (targetUrl) => {
  if (!targetUrl) return false;
  try {
    const rawString =
      typeof targetUrl === "string"
        ? targetUrl
        : targetUrl instanceof URL
          ? targetUrl.href
          : targetUrl && typeof targetUrl === "object" && "url" in targetUrl
            ? String(targetUrl.url)
            : "";
    if (!rawString) return false;
    const origin =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : "http://localhost";
    const parsed = new URL(
      rawString.startsWith("//") ? "https:" + rawString : rawString,
      origin
    );
    return parsed.hostname === CDN_HOSTNAME;
  } catch (e) {
    return false;
  }
};

export const getPathname = (url) => {
  if (typeof url !== "string") return "";
  try {
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("//")
    ) {
      return new URL(url).pathname;
    }
    return url.split("?")[0];
  } catch (e) {
    return url;
  }
};

export const isAbsoluteUrl = (url) =>
  typeof url === "string" &&
  (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("//"));

export const isSameOriginOrRelative = (url) => {
  if (typeof url !== "string") return false;
  if (!isAbsoluteUrl(url)) {
    return url.startsWith("/");
  }
  try {
    const origin =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : "http://localhost";
    const parsed = new URL(url.startsWith("//") ? "https:" + url : url, origin);
    return parsed.origin === origin;
  } catch (e) {
    return false;
  }
};

export const isBackendRequest = (url, pathname, backendUrl) => {
  if (!backendUrl) return false;

  const isAbsolute =
    backendUrl.startsWith("http://") ||
    backendUrl.startsWith("https://") ||
    backendUrl.startsWith("//");

  if (isAbsolute) {
    try {
      const origin =
        typeof window !== "undefined" && window.location
          ? window.location.origin
          : "http://localhost";
      const parsedRequest = new URL(
        url.startsWith("//") ? "https:" + url : url,
        origin
      );
      const parsedBackend = new URL(
        backendUrl.startsWith("//") ? "https:" + backendUrl : backendUrl,
        origin
      );

      return (
        parsedRequest.origin === parsedBackend.origin &&
        parsedRequest.pathname.startsWith(parsedBackend.pathname)
      );
    } catch (e) {
      return false;
    }
  }

  return isSameOriginOrRelative(url) && pathname.startsWith(backendUrl);
};

export const isBackend = (url) => {
  const pathname = getPathname(url);
  const backendUrl = BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";
  const aeBackendUrl = AE_BACKEND_URL || process.env.REACT_APP_AE_BACKEND_URL || "";
  const authIssuer = getAuthIssuer();

  const isExplicitBackend =
    isBackendRequest(url, pathname, backendUrl) ||
    isBackendRequest(url, pathname, aeBackendUrl) ||
    isBackendRequest(url, pathname, authIssuer);

  if (isExplicitBackend) return true;

  if (isSameOriginOrRelative(url)) {
    return (
      pathname.startsWith("/backend/") ||
      pathname.startsWith("/ae-backend/") ||
      pathname.startsWith("/auth/") ||
      pathname.startsWith("/api/v1/") ||
      pathname.startsWith("/api/v2/") ||
      pathname.startsWith("/ai-assistant/")
    );
  }

  return false;
};

/**
 * Authentication Endpoint Policies:
 * - /login & /refresh: Establishing or refreshing credentials.
 *   NEVER inject Bearer tokens, and NEVER trigger 401 refresh retries.
 * - /logout: Invalidating active session on backend.
 *   DO inject Bearer token, but NEVER trigger 401 refresh retries (if token is already expired, logout is complete).
 */
export const isLoginOrRefreshEndpoint = (url) => {
  const pathname = getPathname(url);
  const cleanUrl = typeof url === "string" ? url : "";
  const authIssuer = getAuthIssuer();
  const isOtpEndpoint =
    /(?:^|\/)api\/v1\/auth\/otp-login(?:\/|$)/.test(pathname) ||
    /(?:^|\/)auth\/otp-login(?:\/|$)/.test(pathname) ||
    pathname === `${authIssuer}/otp-login` ||
    pathname.startsWith(`${authIssuer}/otp-login/`) ||
    cleanUrl === `${authIssuer}/otp-login` ||
    cleanUrl.startsWith(`${authIssuer}/otp-login/`) ||
    cleanUrl.startsWith(`${authIssuer}/otp-login?`);
  return (
    pathname === `${authIssuer}/refresh` ||
    pathname === `${authIssuer}/login` ||
    isOtpEndpoint ||
    cleanUrl.startsWith(`${authIssuer}/refresh`) ||
    cleanUrl.startsWith(`${authIssuer}/login`)
  );
};

export const isLogoutEndpoint = (url) => {
  const pathname = getPathname(url);
  const cleanUrl = typeof url === "string" ? url : "";
  const authIssuer = getAuthIssuer();
  return (
    pathname === `${authIssuer}/logout` ||
    cleanUrl.startsWith(`${authIssuer}/logout`)
  );
};

export const isResetPasswordEndpoint = (url) => {
  const pathname = getPathname(url);
  const cleanUrl = typeof url === "string" ? url : "";
  const authIssuer = getAuthIssuer();
  return (
    pathname === `${authIssuer}/reset-password` ||
    cleanUrl === `${authIssuer}/reset-password` ||
    cleanUrl.startsWith(`${authIssuer}/reset-password?`) ||
    /(?:^|\/)auth\/reset-password(?:\/|$)/.test(pathname)
  );
};

// Request interceptor: Skip token injection ONLY for login and refresh endpoints
export const shouldSkipTokenInjection = (url) => isLoginOrRefreshEndpoint(url);

// Response interceptor: Skip 401 refresh retry for login, refresh, logout, AND reset-password endpoints
export const shouldSkip401Refresh = (url) =>
  isLoginOrRefreshEndpoint(url) || isLogoutEndpoint(url) || isResetPasswordEndpoint(url);
